import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import DashboardShell from "@/components/DashboardShell";
import api, { formatError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Flag, Car, Trophy, ArrowLeft, AlertTriangle } from "lucide-react";

/**
 * Recon Race — full-page race-track visualization of every used vehicle
 * moving toward the "Front Line Ready" checkered flag. Same data source
 * as `/posse` (posse_inventory) but with the speedway blown up to hero
 * proportions so the GM can watch progress at a glance.
 */

const BRAND_BRONZE = "#a06a1e";
const BRAND_BRONZE_DARK = "#7a4e0f";
const BRAND_CREAM = "#f6ecd8";

function money(n) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(n || 0);
}

export default function ReconRacePage() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api
      .get("/posse/inventory")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatError(e.response?.data?.detail)));
  }, []);

  const grouped = useMemo(() => {
    const g = { in_pipeline: 0, past_due: 0, front_line: 0, sold: 0 };
    for (const v of data?.vehicles || []) {
      if (v.status === "sold") g.sold += 1;
      else if (v.status === "front_line") g.front_line += 1;
      else {
        g.in_pipeline += 1;
        if (v.days_in_recon > 7) g.past_due += 1;
      }
    }
    return g;
  }, [data]);

  const cars = (data?.vehicles || []).filter((v) => v.status !== "sold");
  const soldCars = (data?.vehicles || []).filter((v) => v.status === "sold");

  return (
    <DashboardShell>
      {/* Hero */}
      <div
        className="mb-4 rounded-xl overflow-hidden shadow-xl relative"
        style={{
          background: `linear-gradient(135deg, ${BRAND_BRONZE_DARK} 0%, ${BRAND_BRONZE} 60%, #c98c3d 100%)`,
        }}
        data-testid="recon-race-hero"
      >
        <div className="relative p-5 md:p-6 flex items-center gap-4 flex-wrap">
          <div
            className="w-14 h-14 rounded-full grid place-items-center shadow-xl shrink-0"
            style={{ background: BRAND_CREAM }}
          >
            <Flag
              className="w-7 h-7"
              strokeWidth={2.5}
              style={{ color: BRAND_BRONZE_DARK }}
            />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-mono-label text-[0.6rem] text-white/85 tracking-widest">
              PREOWNED PIPELINE · RECON RACE
            </div>
            <div className="font-display font-black text-3xl md:text-4xl tracking-tighter text-white leading-none mt-1">
              Recon Race
            </div>
            <div className="font-display text-white/90 text-sm md:text-base mt-1">
              Every used car racing toward front-line. Green cars have crossed the finish line, red cars are stuck &gt; 7 days.
            </div>
            <div className="font-display text-white/95 text-xs md:text-sm mt-2">
              {grouped.in_pipeline} car{grouped.in_pipeline === 1 ? "" : "s"} running · {grouped.front_line} on the front line ·{" "}
              {grouped.past_due > 0 && (
                <span className="text-red-100">🚨 {grouped.past_due} delayed</span>
              )}
              {grouped.past_due === 0 && <span>🟢 pipeline clean</span>}
            </div>
          </div>
          <div className="flex gap-2">
            <Link to="/posse">
              <Button
                variant="outline"
                className="bg-white/95 hover:bg-white text-neutral-900 border-transparent"
                data-testid="recon-race-back-posse"
              >
                <ArrowLeft className="w-4 h-4 mr-1.5" strokeWidth={1.75} />
                Back to Pipeline
              </Button>
            </Link>
            {grouped.past_due > 0 && (
              <Link to="/recon-recovery">
                <Button
                  className="bg-red-600 hover:bg-red-500 text-white border-transparent"
                  data-testid="recon-race-open-recovery"
                >
                  <AlertTriangle className="w-4 h-4 mr-1.5" strokeWidth={2} />
                  Delayed Vehicles ({grouped.past_due})
                </Button>
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Brief on-track legend — what am I looking at? */}
      <Card className="p-4 mb-4 border-2 border-neutral-300 bg-neutral-50 dark:bg-neutral-900/50" data-testid="recon-race-legend">
        <div className="font-mono-label text-[0.65rem] tracking-widest text-neutral-600 mb-2">
          WHAT AM I LOOKING AT?
        </div>
        <p className="text-sm text-neutral-800 dark:text-neutral-200 leading-relaxed mb-3">
          Every used car we take in as a trade or from auction starts at <b>ACQUIRED</b> and
          races across the track through <b>IN RECON</b> → <b>MERCHANDISING</b> → <b>FRONT LINE</b>.
          The faster a car crosses the finish line, the sooner it's making money on the lot.
          Every day it sits in recon costs the dealer holding money.
        </p>
        <div className="flex flex-wrap gap-x-6 gap-y-2 text-xs text-neutral-700 dark:text-neutral-300 items-center pt-2 border-t border-neutral-200 dark:border-neutral-800">
          <div className="font-mono-label text-[0.6rem] tracking-widest text-neutral-500">READ THE CARS:</div>
          <div className="flex items-center gap-1.5"><Car className="w-4 h-4 text-[#f6ecd8]" strokeWidth={2.4}/> = in the pipeline</div>
          <div className="flex items-center gap-1.5"><Car className="w-4 h-4 text-emerald-500" strokeWidth={2.4}/> = front-line ready</div>
          <div className="flex items-center gap-1.5"><Car className="w-4 h-4 text-red-500" strokeWidth={2.4}/> = stuck &gt; 7 days (fix now)</div>
          <div className="flex items-center gap-1.5">🏁 = finish · <b>Victory Lane below = cars sold this week</b></div>
        </div>
      </Card>

      {/* Big track */}
      <Card
        className="p-5 mb-4 border-2 overflow-hidden"
        style={{ borderColor: BRAND_BRONZE_DARK, background: "#0a0a0a" }}
        data-testid="recon-race-track"
      >
        {/* Wave of checker flags — bunting fluttering above the track */}
        <div className="relative h-10 mb-2 select-none" aria-hidden data-testid="recon-race-flag-wave">
          {Array.from({ length: 24 }).map((_, i) => {
            const left = (i / 23) * 100;
            const wave = Math.sin(i * 0.75) * 8 + 10;
            return (
              <div
                key={i}
                className="absolute origin-top"
                style={{
                  left: `${left}%`,
                  top: `${wave}px`,
                  transform: "translateX(-50%)",
                  animation: `raceFlutter${i % 3} 1.6s ease-in-out ${i * 0.06}s infinite alternate`,
                }}
              >
                <svg width="22" height="18" viewBox="0 0 12 10">
                  <rect x="0" y="0" width="0.8" height="10" fill="#a06a1e" />
                  <rect x="1" y="0.5" width="5" height="4" fill="#f6ecd8" />
                  <rect x="1" y="0.5" width="2.5" height="2" fill="#000" />
                  <rect x="3.5" y="2.5" width="2.5" height="2" fill="#000" />
                </svg>
              </div>
            );
          })}
          <style>{`
            @keyframes raceFlutter0 { from { transform: translateX(-50%) rotate(-8deg); } to { transform: translateX(-50%) rotate(10deg); } }
            @keyframes raceFlutter1 { from { transform: translateX(-50%) rotate(6deg); } to { transform: translateX(-50%) rotate(-12deg); } }
            @keyframes raceFlutter2 { from { transform: translateX(-50%) rotate(-11deg); } to { transform: translateX(-50%) rotate(7deg); } }
          `}</style>
        </div>

        <div className="relative rounded-lg overflow-hidden" style={{ background: "#1c1c1c" }}>
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(180deg, #262626 0%, #1a1a1a 40%, #1a1a1a 60%, #262626 100%)",
            }}
          />
          {/* Two centerline dashes for a real 2-lane feel */}
          {[35, 65].map((pct) => (
            <div
              key={pct}
              className="absolute inset-x-0 h-[2px] opacity-70"
              style={{
                top: `${pct}%`,
                background:
                  "repeating-linear-gradient(90deg, #f6ecd8 0 22px, transparent 22px 44px)",
              }}
            />
          ))}
          {/* Checkpoint markers */}
          <div className="relative grid grid-cols-4 text-[0.6rem] font-mono-label tracking-widest text-white/70 px-4 pt-2">
            <div>ACQUIRED</div>
            <div>IN RECON</div>
            <div>MERCHANDISING</div>
            <div className="text-right text-[#f6ecd8]">🏁 FRONT LINE</div>
          </div>

          <div className="relative h-[420px]">
            {cars.map((v, i) => {
              const stageBase = {
                acquired: 4,
                in_recon: 30,
                merchandising: 56,
                front_line: 88,
              }[v.status] ?? 4;
              const jitter =
                v.status === "front_line"
                  ? (i % 5) * 1.2
                  : Math.min(18, (v.days_in_recon || 0) * 1.4);
              const left = Math.min(94, stageBase + jitter);
              const lane = i % 5;
              const top = 40 + lane * 70;
              const isPastDue = v.days_in_recon > 7;
              const isFinished = v.status === "front_line";
              return (
                <div
                  key={v.id}
                  className="absolute group cursor-help transition-transform hover:scale-125"
                  style={{ left: `${left}%`, top: `${top}px` }}
                  title={`${v.stock_no} · ${v.year} ${v.make_model} · ${v.responsible_manager} · ${v.days_in_recon}d`}
                  data-testid={`recon-race-car-${v.id}`}
                >
                  <Car
                    className={`w-6 h-6 drop-shadow-lg ${
                      isFinished
                        ? "text-emerald-400"
                        : isPastDue
                        ? "text-red-500 animate-pulse"
                        : "text-[#f6ecd8]"
                    }`}
                    strokeWidth={2.4}
                  />
                  <div className="absolute left-1/2 -translate-x-1/2 -bottom-4 text-[0.55rem] font-mono text-white/60 whitespace-nowrap">
                    {v.stock_no} · {v.days_in_recon}d
                  </div>
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity absolute left-1/2 -translate-x-1/2 -top-16 bg-[#f6ecd8] text-neutral-900 text-[0.6rem] rounded-md px-2 py-1 shadow-xl whitespace-nowrap z-10 pointer-events-none">
                    <div className="font-mono font-bold">{v.stock_no}</div>
                    <div className="italic text-[0.55rem]">
                      {v.year} {v.make_model}
                    </div>
                    <div className="text-[0.55rem] mt-0.5">
                      {v.responsible_manager}
                    </div>
                    <div className="text-[0.55rem] text-neutral-600">
                      Acquired {v.acquired_on} · Target {v.target_frontline_date}
                    </div>
                  </div>
                </div>
              );
            })}
            <div
              className="absolute top-0 bottom-0 w-[10px]"
              style={{
                right: "5%",
                background:
                  "repeating-linear-gradient(0deg, #ffffff 0 12px, #000 12px 24px)",
                boxShadow: "0 0 24px rgba(255,255,255,0.5)",
              }}
              aria-hidden
            />
          </div>
        </div>

        {/* Victory Lane — every car that finished the race & got sold */}
        {soldCars.length > 0 && (
          <div
            className="mt-4 rounded-lg px-3 py-2.5 flex items-center gap-3 overflow-x-auto"
            style={{
              background:
                "linear-gradient(90deg, #7a4e0f 0%, #a06a1e 55%, #c98c3d 100%)",
            }}
            data-testid="recon-race-victory"
          >
            <div className="flex items-center gap-1.5 shrink-0">
              <Trophy className="w-4 h-4 text-yellow-200" strokeWidth={2.4} />
              <span className="font-mono-label text-[0.6rem] tracking-widest text-yellow-100">
                VICTORY LANE · CARS SOLD
              </span>
            </div>
            <div className="flex gap-2">
              {soldCars.map((v) => (
                <div
                  key={v.id}
                  className="shrink-0 rounded-full bg-black/40 px-2 py-1 text-[0.6rem] text-yellow-50 border border-yellow-200/40 whitespace-nowrap"
                  title={`${v.stock_no} · ${v.year} ${v.make_model} · sold gross ${money(v.sold_gross)}`}
                  data-testid={`recon-race-victory-${v.id}`}
                >
                  <Car className="w-3 h-3 inline mr-1 -mt-0.5" strokeWidth={2.4} />
                  {v.stock_no} · {money(v.sold_gross)}
                </div>
              ))}
            </div>
          </div>
        )}
      </Card>

      {/* Cast of Characters removed — real names only, no racing callsigns. */}
    </DashboardShell>
  );
}
